import { createGlobalState } from '@vueuse/shared';
import { definePreset, usePreset } from '@primeuix/themes';
import type { Preset } from '@primeuix/themes/types';
import { ref, type Ref } from 'vue';
import { themeQuartz } from 'ag-grid-community';
import type { AppTheme, DesignTokenTheme } from '@/themes/types';

type AppThemeStoreState = Ref<AppTheme>;

export const useAppThemeGlobalState = createGlobalState((): AppThemeStoreState => {
  return ref<AppTheme>({
    themeTokens: {} as DesignTokenTheme,
    primevuePreset: {} as Preset,
    agGridTheme: themeQuartz,
  });
});

export function useAppTheme(): {
  appTheme: Ref<AppTheme>;
  setAppTheme: (nextTheme: AppTheme) => void;
  updateAppTheme: (nextTheme: AppTheme) => void;
} {
  const appTheme = useAppThemeGlobalState();

  /** Write store only — used at boot before PrimeVue is installed. */
  const setAppTheme = (nextTheme: AppTheme): void => {
    appTheme.value = nextTheme;
  };

  /**
   * Full env / brand swap: replace the active PrimeVue preset.
   * `usePreset` replaces; `updatePreset` only deep-merges (for partial editor edits later).
   * Cast: our `TokenObject` trees are looser than PrimeVue's `ComponentsDesignTokens`.
   */
  const replacePrimevuePreset = (primevuePreset: Preset): void => {
    usePreset(definePreset(primevuePreset));
  };

  /**
   * Replace app theme state and the live PrimeVue preset.
   * Used for env switching (and later dark/light or theme editor full loads).
   */
  const updateAppTheme = (nextTheme: AppTheme): void => {
    setAppTheme(nextTheme);
    replacePrimevuePreset(nextTheme.primevuePreset);
    // AgGrid theme is consumed from `appTheme` by tables — no separate apply step.
  };

  return {
    appTheme,
    setAppTheme,
    updateAppTheme,
  };
}
